import type { PushData } from 'src/entities/push'
import { mechtaIDAuthService } from 'src/adapters/auth/mechta-id.service'
import { pushRoute } from 'src/entities/push'
import { sellerNewsTopics } from 'src/entities/push/news-topics'
import { ROUTES } from 'src/presentation/shared/routes'
import {
  forgetPushDevice,
  logoutPushDevice,
  preparePush,
  pushBridge,
  registerPushDevice,
  syncNewsTopics,
} from 'src/use-cases/push'
import { useAuthStore } from 'stores/auth'
import { useProfileStore } from 'stores/profile'
import { usePushStore } from 'stores/push'
import { computed, watch } from 'vue'
import { useRouter } from 'vue-router'

// deeplink пуша, по которому ещё не перешли: переживает перезагрузку страницы при входе через Mechta ID
const PENDING_KEY = 'push:pending-route'
let newsProfileReady = false
let loggingOut = false

export function syncCurrentNewsTopics(token?: string): Promise<void> {
  const profile = useProfileStore().profile
  if (!newsProfileReady || loggingOut || !useAuthStore().token || !profile) return Promise.resolve()
  const topics = usePushStore().enabled ? sellerNewsTopics(profile.roles, profile.subdivisionUtpId) : []
  return syncNewsTopics.execute(topics, token)
}

function refreshNewsTopics(token?: string) {
  syncCurrentNewsTopics(token).catch(() => console.warn('push: news topics sync failed'))
}

function savePending(route: string) {
  try {
    sessionStorage.setItem(PENDING_KEY, route)
  } catch {
    // хранилище недоступно — переход после входа не случится, приложение просто откроется
  }
}

function takePending(): string | null {
  try {
    const route = sessionStorage.getItem(PENDING_KEY)
    sessionStorage.removeItem(PENDING_KEY)
    return route
  } catch {
    return null
  }
}

/**
 * Записать устройство вошедшего продавца в notifire (external_id из атрибутов Mechta ID + телефон),
 * если пуши не выключены в приложении. token — новый токен от Firebase.
 */
export function registerCurrentDevice(token?: string) {
  const profile = useProfileStore().profile
  if (loggingOut || !usePushStore().enabled || !useAuthStore().token || !profile) return
  // владелец устройства в notifire — external_id продавца, а не sub Mechta ID: по нему пуши шлют сервисы
  const owner = { userId: profile.externalId, phone: profile.phone }
  if (!owner.userId && !owner.phone) {
    console.warn('push: no external_id and phone in the profile — the device is not registered')
    return
  }
  registerPushDevice.execute(owner, token).catch((error) => {
    console.warn('push: device registration failed', error)
  })
}

/**
 * Пуши в приложении-обёртке: при открытии — разрешение и токен FCM, после входа — устройство в
 * notifire (registerCurrentDevice), при выходе — устройство выключается в notifire и токен удаляется,
 * по нажатию на пуш — переход на его deeplink (после входа и PIN), разрешение телефона — в usePushStore.
 * В браузере без обёртки ничего не делает.
 */
export function usePushNotifications() {
  if (!pushBridge.isAvailable()) return

  const router = useRouter()
  const authStore = useAuthStore()
  const profileStore = useProfileStore()
  const pushStore = usePushStore()

  const isAuth = computed(() => !!authStore.token)

  // Переходить можно, когда продавец уже прошёл экран PIN: роутер закончил стартовую навигацию и стоит
  // не на AuthPage. PIN хранится постоянно, поэтому одного authStore.passCode мало — при холодном
  // старте переход перебил бы стартовую навигацию и обошёл бы ввод PIN.
  function canNavigate() {
    const current = router.currentRoute.value
    return isAuth.value && current.matched.length > 0 && current.name !== ROUTES.AUTH
  }

  function open(data: PushData) {
    const route = pushRoute(data)
    if (!route) return
    if (!canNavigate()) {
      savePending(route)
      return
    }
    router.push(route).catch(() => {})
  }

  preparePush.execute()?.catch((error) => {
    console.warn('push: token unavailable', error)
  })

  // Wait for fresh userinfo, not the profile restored from localStorage.
  watch(() => profileStore.profile, (profile) => {
    newsProfileReady = !!profile
    if (profile) loggingOut = false
    registerCurrentDevice()
    refreshNewsTopics()
  })
  watch(() => [pushStore.enabled, profileStore.profile?.subdivisionUtpId, profileStore.profile?.roles.join(',')], () => refreshNewsTopics())

  // разрешение телефона: при старте и при возврате в приложение (могли поменять в настройках);
  // разрешили в настройках — устройство записывается
  if (pushBridge.canManagePermission()) {
    pushBridge.getPermission().then(pushStore.setPermission).catch(() => {})
    pushBridge.onPermissionChanged((permission) => {
      const wasGranted = pushStore.permission === 'granted'
      pushStore.setPermission(permission)
      if (permission === 'granted' && !wasGranted) registerCurrentDevice()
      refreshNewsTopics()
    })
  }

  // «Выйти» и сброс PIN: до сброса сессии, пока запрос в notifire ещё с токеном; следующему продавцу
  // на этом телефоне пуши снова включены
  mechtaIDAuthService.onBeforeLogout(async () => {
    loggingOut = true
    newsProfileReady = false
    // Keep the recorded topics until native unsubscribe completes. Reserve time for
    // device logout within the auth service's three-second before-logout deadline.
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      await Promise.race([
        syncNewsTopics.execute([]),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error('PUSH_UNSUBSCRIBE_TIMEOUT')), 1500)
        }),
      ])
    } catch {
      console.warn('push: news topics unsubscribe failed; deleting device token')
    } finally {
      clearTimeout(timer)
      syncNewsTopics.reset()
    }
    try {
      await logoutPushDevice.execute()
    } finally {
      pushStore.setEnabled(true)
    }
  })

  // сессия кончилась без logout() (refresh не удался, нет доступа): notifire уже не позвать —
  // только токен FCM; notifire выключит устройство при следующей отправке
  watch(isAuth, (value, oldValue) => {
    if (!oldValue || value) return
    newsProfileReady = false
    syncNewsTopics.reset()
    forgetPushDevice.execute()
    // пуш прежнего продавца не открывается следующему
    takePending()
  })

  // первый переход после экрана PIN (на главную или куда вёл роутер) — сразу на deeplink пуша
  router.afterEach((to) => {
    if (to.name === ROUTES.AUTH || !isAuth.value) return
    const route = takePending()
    if (route && route !== to.fullPath) router.push(route).catch(() => {})
  })

  pushBridge.onTokenChanged((token) => {
    registerCurrentDevice(token)
    refreshNewsTopics(token)
  })
  pushBridge.onOpened(open)
  pushBridge.requestInitialPush()
}
